import * as policyEngine from '@/domain/restriction/engine';
import {
  IS_ACCOUNT,
  IS_ADMIN,
  IS_AUTHORIZED,
  IS_COMPANY_SUPPORT_FINANCIAL_DOC,
  IS_USER,
} from '@/domain/restriction/policies';
import { isFirefox } from '@/lib/systemHelpers/browserHelpers';
import { Set } from 'immutable';

export const rights = (notes, doctype, status, restriction, tags = new Set(), linkid, isDocCreator = false) => ({
  notes: () => Boolean(notes.length > 0),
  noNotes: () => notes.length === 0,
  general: () => doctype === 'general',
  financial: () => doctype === 'financial',
  isNew: () => status.includes('new'),
  isNotAccepted: () => !status.includes('accepted'),
  readOnly: () => policyEngine.check(IS_USER, restriction),
  isAuthorized: () => policyEngine.check(IS_AUTHORIZED, restriction),
  isAccount: () => policyEngine.check(IS_ACCOUNT, restriction),
  isAdmin: () => policyEngine.check(IS_ADMIN, restriction),
  isAccepted: () => status.includes('accepted'),
  isScheduledAcceptance: () => tags.has('_S_SCHEDULED_ACCEPTANCE'),
  companyDoesntSupportFinDoc: () => !policyEngine.check(IS_COMPANY_SUPPORT_FINANCIAL_DOC, restriction),
  allowCreateNotes: () => true,
  allowUpdateNotes: () => true,
  notLinked: () => !linkid,
  isDocCreator: () => isDocCreator,
  trashDocument: () => tags.has('_C_TAG_TRASH'),
  notTrashedDocument: () => !tags.has('_C_TAG_TRASH'),
});

export const localRights = policyEngine.advancedRights(rights);

export const items = [
  {
    id: 'addAndLinkDocument',
    title: 'Add & link document',
    action: () => null,
    restriction: localRights.policy([localRights.paramsName.isAuthorized, localRights.paramsName.notTrashedDocument]),
  },
  {
    id: 'open',
    title: 'Open',
    disabled: false,
    action: () => null,
    restriction: localRights.policy([localRights.paramsName.isAuthorized, localRights.paramsName.notTrashedDocument]),
  },
  {
    id: 'preview',
    title: 'Preview',
    action: () => null,
    restriction: localRights.policy([localRights.paramsName.isAuthorized, localRights.paramsName.notTrashedDocument]),
  },
  {
    id: 'setAsCover',
    title: 'Set as cover',
    disabled: false,
    action: () => null,
    restriction: localRights.policy([localRights.paramsName.isAuthorized, localRights.paramsName.notTrashedDocument]),
  },
  {
    id: 'linkDocuments',
    title: 'Link documents',
    disabled: false,
    action: () => null,
    restriction: localRights.policy([localRights.paramsName.isAuthorized, localRights.paramsName.notTrashedDocument]),
    separator: true,
  },
  {
    id: 'editStickyNote',
    title: 'Edit sticky note',
    disabled: false,
    action: () => null,
    restriction: localRights.policy([
      localRights.paramsName.notes,
      localRights.paramsName.allowUpdateNotes,
      localRights.paramsName.notTrashedDocument,
    ]),
  },
  {
    id: 'addStickyNote',
    title: 'Add sticky note',
    disabled: false,
    action: () => null,
    restriction: localRights.policy([
      localRights.paramsName.noNotes,
      localRights.paramsName.allowCreateNotes,
      localRights.paramsName.notTrashedDocument,
    ]),
  },
  {
    id: 'tagsAs',
    title: 'Tags as',
    disabled: false,
    action: () => null,
    restriction: localRights.policy([localRights.paramsName.isAuthorized, localRights.paramsName.notTrashedDocument]),
  },
  {
    id: 'markAsPaid',
    title: 'Mark as paid',
    disabled: false,
    restriction: localRights.policy([localRights.paramsName.isAuthorized, localRights.paramsName.notTrashedDocument]),
  },
  {
    id: 'markAsUnpaid',
    title: 'Mark as unpaid',
    disabled: false,
    restriction: localRights.policy([localRights.paramsName.isAuthorized, localRights.paramsName.notTrashedDocument]),
  },
  {
    id: 'markAsGeneral',
    title: 'Mark as general document',
    disabled: localRights.policy(localRights.paramsName.isAccepted, localRights.paramsName.isScheduledAcceptance),
    action: () => null,
    restriction: localRights.policy([
      localRights.paramsName.isAccount,
      localRights.paramsName.financial,
      localRights.paramsName.notTrashedDocument,
    ]),
  },
  {
    id: 'markAsFinancial',
    title: 'Mark as financial document',
    disabled: localRights.policy(localRights.paramsName.companyDoesntSupportFinDoc),
    action: () => null,
    // Visible for BOTH general (→ classify) and financial (→ change subtype) docs — no doctype gate.
    restriction: localRights.policy([localRights.paramsName.isAccount, localRights.paramsName.notTrashedDocument]),
  },
  {
    id: 'ignoreDocumentWarnings',
    title: 'Ignore warnings',
    disabled: false,
    action: () => null,
    restriction: localRights.policy([localRights.paramsName.isAuthorized, localRights.paramsName.notTrashedDocument]),
    separator: true,
  },
  {
    id: 'signDocument',
    title: 'Sign document',
    disabled: false,
    action: () => null,
    restriction: localRights.policy([localRights.paramsName.isAuthorized, localRights.paramsName.notTrashedDocument]),
    separator: true,
  },
  {
    id: 'sendByEmail',
    title: 'Share via email',
    disabled: false,
    action: () => null,
    restriction: localRights.policy([localRights.paramsName.isAuthorized, localRights.paramsName.notTrashedDocument]),
  },
  {
    id: 'downloadDocument',
    title: 'Download document',
    disabled: false,
    action: () => null,
    restriction: localRights.policy([localRights.paramsName.isAuthorized, localRights.paramsName.notTrashedDocument]),
  },
  {
    id: 'editDocument',
    title: 'Edit document',
    disabled: false,
    action: () => null,
    restriction: localRights.policy([localRights.paramsName.isAuthorized, localRights.paramsName.notTrashedDocument]),
  },
  {
    id: 'moveTo',
    title: 'Move to',
    disabled: false,
    action: () => null,
    restriction: localRights.policy([localRights.paramsName.isAccount, localRights.paramsName.notTrashedDocument]),
  },
  {
    id: 'linkedBundle',
    title: 'Linked bundle',
    disabled: false,
    action: () => null,
    restriction: localRights.policy([localRights.paramsName.isAuthorized, localRights.paramsName.notTrashedDocument]),
  },
  {
    id: 'moveToCompany',
    title: 'Move to other company',
    action: () => null,
    restriction: localRights.policy([localRights.paramsName.isAccount, localRights.paramsName.notTrashedDocument]),
  },
  {
    id: 'duplicateDocument',
    title: 'Duplicate document',
    disabled: false,
    action: () => null,
    restriction: localRights.policy([localRights.paramsName.isAuthorized, localRights.paramsName.notTrashedDocument]),
  },
  {
    id: 'printDocument',
    title: 'Print document',
    disabled: isFirefox,
    action: () => null,
    restriction: localRights.policy([localRights.paramsName.isAuthorized, localRights.paramsName.notTrashedDocument]),
  },
  {
    id: 'deleteDocument',
    title: 'Delete document',
    disabled: false,
    action: () => null,
    restriction: localRights.policy(
      [localRights.paramsName.isAccount, localRights.paramsName.notTrashedDocument],
      [
        localRights.paramsName.readOnly,
        localRights.paramsName.isNotAccepted,
        localRights.paramsName.notTrashedDocument,
      ],
    ),
    separator: true,
  },
  {
    id: 'restore',
    title: 'Restore',
    disabled: false,
    action: () => null,
    restriction: localRights.policy([localRights.paramsName.isAuthorized, localRights.paramsName.trashDocument]),
  },
  {
    id: 'restoreTo',
    title: 'Restore to',
    disabled: false,
    action: () => null,
    restriction: localRights.policy([localRights.paramsName.isAccount, localRights.paramsName.trashDocument]),
    separator: true,
  },
  {
    id: 'permanentlyDelete',
    title: 'Permanently delete',
    disabled: false,
    action: () => null,
    restriction: localRights.policy(
      [localRights.paramsName.isAdmin, localRights.paramsName.trashDocument],
      [localRights.paramsName.isDocCreator, localRights.paramsName.trashDocument],
    ),
  },
];

export const linkedMenuItems = [
  {
    id: 'addAndLinkDocument',
    title: 'Add & link document',
    action: () => null,
    restriction: localRights.policy(localRights.paramsName.isAuthorized),
  },
  {
    id: 'editLinkDocuments',
    title: 'Edit linked documents',
    disabled: false,
    action: () => null,
    restriction: localRights.policy(localRights.paramsName.isAccount),
  },
  {
    id: 'sendByEmailLinked',
    title: 'Share bundle via email',
    disabled: false,
    action: () => null,
    restriction: localRights.policy(localRights.paramsName.isAuthorized),
  },
  {
    id: 'downloadLinked',
    title: 'Download bundle',
    disabled: false,
    action: () => null,
    restriction: localRights.policy(localRights.paramsName.isAccount),
  },
  {
    id: 'printLinked',
    title: 'Print bundle',
    disabled: false,
    action: () => null,
    restriction: localRights.policy(localRights.paramsName.isAccount),
  },
  {
    id: 'unlink',
    title: 'Unlink documents',
    disabled: false,
    action: () => null,
    restriction: localRights.policy(localRights.paramsName.isAuthorized),
  },
  {
    id: 'moveToBundle',
    title: 'Move bundle to',
    disabled: false,
    action: () => null,
    restriction: localRights.policy([localRights.paramsName.isAccount]),
  },
  {
    id: 'moveLinkedToCompany',
    title: 'Move bundle to company',
    action: () => null,
    restriction: localRights.policy(localRights.paramsName.isAccount),
  },
];

export const linkedBundleSubmenu = [
  {
    id: 'moveToBundle',
    title: 'Move bundle to',
    disabled: false,
    action: () => null,
    restriction: localRights.policy([localRights.paramsName.isAccount]),
  },
  {
    id: 'moveLinkedToCompany',
    title: 'Move bundle to company',
    action: () => null,
    restriction: localRights.policy(localRights.paramsName.isAccount),
  },
  {
    id: 'printLinked',
    title: 'Print bundle',
    disabled: false,
    action: () => null,
    restriction: localRights.policy(localRights.paramsName.isAuthorized),
  },
  {
    id: 'sendByEmailLinked',
    title: 'Share bundle via email',
    disabled: false,
    action: () => null,
    restriction: localRights.policy(localRights.paramsName.isAuthorized),
  },
  {
    id: 'downloadLinked',
    title: 'Download bundle',
    disabled: false,
    action: () => null,
    restriction: localRights.policy(localRights.paramsName.isAuthorized),
  },
];

export const filteredLinkedItem = (d) =>
  linkedMenuItems.filter(
    policyEngine.arrayFilter(localRights.restriction(d.notes, d.doctype, d.status, d.restriction, d.tags)),
  );

export const filteredItem = (d, isDocCreator) => {
  const lr = localRights.restriction(d.notes, d.doctype, d.status, d.restriction, d.tags, d.linkid, isDocCreator);

  const submenus = {
    linkedBundle: {
      submenu: linkedBundleSubmenu.filter(policyEngine.arrayFilter(lr)),
    },
  };

  return items
    .map((item) => ({ ...item, ...submenus[item.id] }))
    .filter(policyEngine.arrayFilter(lr))
    .map(policyEngine.arrayMap(lr, 'disabled'));
};
